import { API } from 'common/constants/urls';
import { del, get, post, put } from '../../../common/axiosClient';
import {
  MagicReportDTO,
  CreateMagicReportDTO,
  UpdateMagicReportDTO,
  MagicReportInvoiceDTO,
  CreateMagicReportInvoiceDTO,
  UpdateMagicReportInvoiceDTO,
  MagicReportPhotoDTO,
  MagicReportPhotoUploadResponse,
  MagicReportApplyRevisionResult,
  MagicReportRevisionList,
  MagicReportRevisionPreview,
  MagicReportRevisionSummary,
} from './magic-report.type';
import { PdfImageQuality } from '../shared/utils/pdfCompression';
import { PdfExportResult, requestPdfExport } from '../shared/utils/pdfExportRequest';
import { versionedRequest } from './magic-report-version';

export const createMagicReport = async (createDto: CreateMagicReportDTO) =>
  (await post(`${API.MAGIC_REPORTS}`, createDto)) as MagicReportDTO;
// Reads and overwriting writes go through versionedRequest so the stale-save check sees them.
/** Pass `trackVersion` only when loading the report into the editor (the wizard); see versionedRequest. */
export const getMagicReport = async (id: string, { trackVersion = false }: { trackVersion?: boolean } = {}) =>
  versionedRequest<MagicReportDTO>(id, 'get', `${API.MAGIC_REPORTS}/${id}`, undefined, { remember: trackVersion });
export const updateMagicReport = async (id: string, updateDto: UpdateMagicReportDTO) =>
  versionedRequest<MagicReportDTO>(id, 'put', `${API.MAGIC_REPORTS}/${id}`, updateDto);
export const deleteMagicReport = async (id: string) => (await del(`${API.MAGIC_REPORTS}/${id}`)) as string;

export const getAllMagicReports = async () => (await get(`${API.MAGIC_REPORTS}`)) as MagicReportDTO[];
export const getMagicReportsByProject = async (projectId: string) =>
  (await get(`${API.MAGIC_REPORTS}?projectId=${projectId}`)) as MagicReportDTO[];
export const resetMagicReport = async (
  magicReportId: string,
  projectId: string,
  name: string,
  drawIds?: string[],
  // Omitted (not just empty) when the caller has no opinion, so the backend carries the replaced
  // report's hidden set forward instead of treating this as "unhide everything".
  hiddenCoLienDrawIds?: string[],
) =>
  (await post(`${API.MAGIC_REPORTS}/reset/${magicReportId}`, {
    projectId,
    name,
    drawIds,
    hiddenCoLienDrawIds,
  })) as MagicReportDTO;

// Invoices
export const saveMagicReportInvoice = async (magicReportId: string, createDto: CreateMagicReportInvoiceDTO) =>
  (await post(`${API.MAGIC_REPORTS}/${magicReportId}/invoices`, createDto)) as MagicReportInvoiceDTO;
export const updateMagicReportInvoice = async (magicReportId: string, invoiceId: string, updateDto: UpdateMagicReportInvoiceDTO) =>
  (await put(`${API.MAGIC_REPORTS}/${magicReportId}/invoices/${invoiceId}`, updateDto)) as MagicReportInvoiceDTO;
export const getMagicReportCurrentInvoice = async (magicReportId: string) =>
  (await get(`${API.MAGIC_REPORTS}/${magicReportId}/current-invoice`)) as MagicReportInvoiceDTO;

// Photos
export const uploadPhoto = async (magicReportId: string, file: File, onUploadProgress?: (progress: number) => void, sortOrder?: number) => {
  const formData = new FormData();
  formData.append('file', file);
  const url =
    sortOrder !== undefined
      ? `${API.MAGIC_REPORTS}/${magicReportId}/photos?sortOrder=${sortOrder}`
      : `${API.MAGIC_REPORTS}/${magicReportId}/photos`;
  const response = (await post(url, formData, {
    onUploadProgress: (p: any) => onUploadProgress?.(Math.round((p.loaded / (p.total || 1)) * 100)),
  })) as MagicReportPhotoUploadResponse;
  return response;
};

export const getPhotos = async (magicReportId: string) => {
  const photos = (await get(`${API.MAGIC_REPORTS}/${magicReportId}/photos`)) as any[];
  return photos.map(photo => ({
    ...photo,
    blobName: photo.pdfBlobName || photo.blobName || photo.originalBlobName,
  })) as MagicReportPhotoDTO[];
};

export const updatePhotoCaption = async (magicReportId: string, photoId: string, caption: string) =>
  (await put(`${API.MAGIC_REPORTS}/${magicReportId}/photos/${photoId}/caption`, { caption })) as string;

export const reorderPhotos = async (magicReportId: string, newOrder: string[]) =>
  (await put(`${API.MAGIC_REPORTS}/${magicReportId}/photos/reorder`, newOrder)) as string;

export const batchUpdatePhotos = async (magicReportId: string, dto: any) =>
  versionedRequest<string>(magicReportId, 'put', `${API.MAGIC_REPORTS}/${magicReportId}/photos/batch-update`, dto);

export const deletePhoto = async (magicReportId: string, photoId: string) =>
  (await del(`${API.MAGIC_REPORTS}/${magicReportId}/photos/${photoId}`)) as string;

export const updateEditedPhoto = async (magicReportId: string, photoId: string, file: File | Blob) => {
  const formData = new FormData();
  // Give the file a proper name so the backend stores fileName correctly.
  // Blobs passed without a name default to "blob" which breaks export lookups.
  const hasValidName = file instanceof File && file.name && file.name !== 'blob';
  const namedFile = hasValidName ? file : new File([file], 'edited-photo.jpg', { type: (file as File).type || 'image/jpeg' });
  formData.append('file', namedFile);
  const photo = (await put(`${API.MAGIC_REPORTS}/${magicReportId}/photos/${photoId}/edit`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })) as MagicReportPhotoDTO;
  return photo;
};

// Export
export const exportMagicReportPdf = async (html: string, addBorder = true, imageQuality?: PdfImageQuality): Promise<PdfExportResult> =>
  requestPdfExport(`${API.MAGIC_REPORTS}/export`, html, addBorder, imageQuality);

// Images/signatures for export
export const getImageAsBase64 = async (blobName: string) => (await get(`project/files/${encodeURIComponent(blobName)}/base64`)) as string;
// The timestamp defeats any HTTP cache entry keyed on this URL (browser or shared proxy), so a
// re-uploaded signature can never be shadowed by the previous one in an exported report.
export const getSignatureAsBase64 = async () => (await get(`/user/signature/base64?t=${Date.now()}`)) as { dataUrl: string };

// Sections
export const getMagicReportSections = async (magicReportId: string) =>
  (await get(`${API.MAGIC_REPORTS}/${magicReportId}/sections`)) as import('./magic-report.type').MagicReportSectionsDTO;

export const updateMagicReportSections = async (
  magicReportId: string,
  updateDto: import('./magic-report.type').UpdateMagicReportSectionsDTO,
) =>
  versionedRequest<import('./magic-report.type').MagicReportSectionsDTO>(
    magicReportId,
    'put',
    `${API.MAGIC_REPORTS}/${magicReportId}/sections`,
    updateDto,
  );

// Version history
export const getMagicReportVersions = async (magicReportId: string, namedOnly = false) =>
  (await get(`${API.MAGIC_REPORTS}/${magicReportId}/versions${namedOnly ? '?namedOnly=true' : ''}`)) as MagicReportRevisionList;
export const getMagicReportVersion = async (magicReportId: string, revisionId: string) =>
  (await get(`${API.MAGIC_REPORTS}/${magicReportId}/versions/${revisionId}`)) as MagicReportRevisionPreview;
/** Puts the whole report back as it was at a version (recorded as a new "Restored" version). */
export const restoreMagicReportVersion = async (magicReportId: string, revisionId: string) =>
  versionedRequest<MagicReportApplyRevisionResult>(
    magicReportId,
    'post',
    `${API.MAGIC_REPORTS}/${magicReportId}/versions/${revisionId}/restore`,
  );
/**
 * Puts back only what one version changed, as that version left it. Throws RevertConflictError when
 * later versions changed the same sections; then pass skipConflicts (leave them) or overwriteConflicts.
 */
export const restoreMagicReportVersionChanges = async (
  magicReportId: string,
  revisionId: string,
  options: { skipConflicts?: boolean; overwriteConflicts?: boolean; sinceRevisionId?: string } = {},
) =>
  versionedRequest<MagicReportApplyRevisionResult>(
    magicReportId,
    'post',
    `${API.MAGIC_REPORTS}/${magicReportId}/versions/${revisionId}/restore-changes`,
    { skipConflicts: !!options.skipConflicts, overwriteConflicts: !!options.overwriteConflicts, sinceRevisionId: options.sinceRevisionId },
  );
export const nameMagicReportVersion = async (magicReportId: string, revisionId: string, name: string) =>
  (await post(`${API.MAGIC_REPORTS}/${magicReportId}/versions/${revisionId}/name`, { name })) as MagicReportRevisionSummary;
export const removeMagicReportVersionName = async (magicReportId: string, revisionId: string) =>
  (await del(`${API.MAGIC_REPORTS}/${magicReportId}/versions/${revisionId}/name`)) as MagicReportRevisionSummary;

/** Ends the open revision when the user leaves a main wizard step without anything to save. */
export const closeMagicReportRevision = async (magicReportId: string) => post(`${API.MAGIC_REPORTS}/${magicReportId}/versions/close`, {});
/** Records a fixed point in the history: just before "Run this Month", or an export. */
export const createMagicReportCheckpoint = async (magicReportId: string, kind: 'RunThisMonth' | 'Export') =>
  post(`${API.MAGIC_REPORTS}/${magicReportId}/versions/checkpoint`, { kind });
